import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { createUseStyles } from 'react-jss';
import { ThemeContext } from 'styled-components';

import { resultsConfigSelector, quoteFormSelector, hasProductsWithExcessHigherThanFilterValue } from 'ctm-selectors';
import { getBreakpointFromManor } from 'ctm-components';

import { connect } from 'react-redux';
import { Typography } from '@comparethemarketau/manor-typography';
import { Notification } from '@comparethemarketau/manor-notification';

import { HideButon } from './common/hide_button';
import { ContainerWrapper } from '../../container_wrapper';

const useStyles = createUseStyles((theme) => ({
    container: {
        display: 'flex',
        flexDirection: 'row',
        backgroundColor: theme.colors.primary50,
        alignItems: ({ isHideHighExcess, breakpoint }) => (isHideHighExcess && breakpoint !== 'xs' && 'center'),
        extend: ({ breakpoint }) => {
            switch (breakpoint) {
                case 'lg': return {
                    padding: '1.3rem 1.9rem'
                };
                case 'md': return {
                    padding: '1.3rem 1.7rem'
                };
                case 'sm': return {
                    padding: '1.3rem 1.1rem'
                };
                default: return {
                    paddingTop: '1.3rem',
                    paddingBottom: '1rem',
                    paddingLeft: theme.spacing[12]
                };
            }
        }
    },
    notificationBanner: {
        padding: '0.9rem 0px 1.3rem 0px',
        '&& div + div > div[type=alertBanner]': {
            display: 'block'
        }
    },
    leftSection: {
        marginRight: theme.spacing[12]
    },
    notificationContentBody: {
        marginBottom: ({ breakpoint }) => {
            if (breakpoint === 'xs' || breakpoint === 'sm') {
                return '1rem';
            }
            return theme.spacing[0];
        }
    },
    notificationContentArea: {
        display: 'flex',
        justifyContent: 'space-between',
        flexWrap: ({ breakpoint }) => {
            if (breakpoint === 'xs' || breakpoint === 'sm') {
                return 'wrap';
            }
            return 'no-wrap';
        }
    },
    notificationButtonContainer: {
        display: 'inline-flex'
    },
    notificationTitle: {
        fontSize: theme.fontSize.sm,
        fontWeight: theme.fontWeight.bold
    },
    notificationContent: {
        fontSize: theme.fontSize.xs
    },
    noticeSection: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        rowGap: theme.spacing[12],
        columnGap: theme.spacing[12],
        justifyContent: 'space-between',
        flex: '1',
        alignItems: ({ isHideHighExcess }) => (isHideHighExcess && 'center')
    }
}));

const _HigherExcessDisclaimer = ({ isHideHighExcess, selectedExcessValue, hasHighExcess, padding }) => {
    const theme = useContext(ThemeContext);
    const breakpoint = getBreakpointFromManor();
    const classes = useStyles({ theme, breakpoint, isHideHighExcess });

    const notificationContent = (
        <div className={classNames('notificationContentArea', classes.notificationContentArea)}>
            <div className={classNames('notificationContentBody', classes.notificationContentBody)}>
                <div className="titleSection"><Typography className={classes.notificationTitle}>{`Your excess amount is set to $${selectedExcessValue}`}</Typography></div>
                <div className="bodySection">
                    {
                        !isHideHighExcess
                && <Typography className={classes.notificationContent}>We may also show you products that have a higher excess, to provide you with a larger selection of competitive products.</Typography>
                    }
                </div>
            </div>
            <div className={classNames('notificationButtonContainer', classes.notificationButtonContainer)}>
                <div className={classNames('notificationButtonArea', classes.notificationButtonArea)}>
                    <HideButon selectedExcessValue={selectedExcessValue} isHideHighExcess={isHideHighExcess} />
                </div>
            </div>
        </div>
    );

    return (
        (
            <>
                { hasHighExcess
            && (
                <ContainerWrapper padding={padding} className={classNames('test-higherExcessDisclaimer')}>
                    <Notification
                        trackingLabel="Higher Excess notification"
                        type="alertBanner"
                        variant="general"
                        icon
                        background
                        content={notificationContent}
                        className={classNames('higherExcessNotificationBanner', classes.notificationBanner)} />
                </ContainerWrapper>
            )}
            </>
        )
    );
};

_HigherExcessDisclaimer.propTypes = {
    isHideHighExcess: PropTypes.bool,
    hasHighExcess: PropTypes.bool,
    selectedExcessValue: PropTypes.string,
    padding: PropTypes.array
};

// eslint-disable-next-line valid-jsdoc
/**
 * see /packages/ctm-vertical-car/components/results/welcome_message/welcome_message.js
 */
const mapStateToProps = (state) => {
    const selectedExcessValue = resultsConfigSelector(state).getIn('filters.requestedExcess'.split('.'));
    const form = quoteFormSelector(state);
    const excessFilterValue = form.getIn(['coverDetail', 'requestedExcess']);
    const isHideHighExcess = state.getIn(['page', 'isHideHighExcess']);
    return {
        isHideHighExcess,
        hasHighExcess: hasProductsWithExcessHigherThanFilterValue(state, excessFilterValue),
        selectedExcessValue
    };
};

const HigherExcessDisclaimer = (connect(mapStateToProps)(_HigherExcessDisclaimer));

export { HigherExcessDisclaimer, _HigherExcessDisclaimer };
